/**
 * 頁面路徑到使用說明文檔的映射配置
 * 根據當前路徑返回對應的說明文檔 URL
 *
 * 新的 Docs 架構：
 * - 文檔位置：/docs/{role}/{feature}/{action}
 * - Markdown 文件：public/docs/{locale}/{role}-{feature}-{action}.md
 * - 例如：Admin 商品列表 → /docs/admin/products/list → public/docs/zh/admin-products-list.md
 */

/**
 * Admin 角色的文檔映射
 * 路徑格式：實際頁面路徑 → Docs 文檔路徑
 *
 * 範例：
 * '/products' → '/docs/admin/products/list'
 * '/products/new' → '/docs/admin/products/new'
 * '/products/:id' → '/docs/admin/products/show'
 * '/products/:id/edit' → '/docs/admin/products/edit'
 */
const adminDocsMap = {
  // Products
  '/products': '/docs/admin/products/list',
  '/products/new': '/docs/admin/products/new',
  '/products/:id': '/docs/admin/products/show',
  '/products/:id/edit': '/docs/admin/products/edit',

  // Brands
  '/brands': '/docs/admin/brands/list',
  '/brands/new': '/docs/admin/brands/new',
  '/brands/:id': '/docs/admin/brands/show',

  // Bundles
  '/bundles': '/docs/admin/bundles/list',
  '/bundles/new': '/docs/admin/bundles/new',
  '/bundles/:id': '/docs/admin/bundles/show',
  '/bundles/:id/edit': '/docs/admin/bundles/edit',

  // Flexible Products
  '/flexible_products': '/docs/admin/flexible_products/list',
  '/flexible_products/new': '/docs/admin/flexible_products/new',
  '/flexible_products/:id': '/docs/admin/flexible_products/show',
  '/flexible_products/:id/edit': '/docs/admin/flexible_products/edit',

  // Contact Addresses
  '/contact_addresses': '/docs/admin/contact_addresses/list',
  '/contact_addresses/new': '/docs/admin/contact_addresses/new',
  '/contact_addresses/:id': '/docs/admin/contact_addresses/show',
  '/contact_addresses/:id/edit': '/docs/admin/contact_addresses/edit',

  // Orders
  '/orders': '/docs/admin/orders/list',
  '/orders/new': '/docs/admin/orders/new',
  '/orders/:id': '/docs/admin/orders/show',
  '/orders/:id/edit': '/docs/admin/orders/edit',

  // Out of Stock Products
  '/orders/out_of_stocks': '/docs/admin/out_of_stock_products/list',

  // Sales Analytics
  '/orders/analytics': '/docs/admin/orders/analytics',

  // Fulfillment Merge
  '/fulfillments/merge': '/docs/admin/fulfillments/merge',

  // Order Serial Numbers
  '/orders/serial_numbers': '/docs/admin/orders/serial_numbers',

  // Order Templates
  '/order_templates': '/docs/admin/order_templates/list',
  '/order_templates/new': '/docs/admin/order_templates/new',
  '/order_templates/:id': '/docs/admin/order_templates/show',
  '/order_templates/:id/edit': '/docs/admin/order_templates/edit',

  // Order Post Processors
  '/order_post_processors': '/docs/admin/order_post_processors/list',
  '/order_post_processors/new': '/docs/admin/order_post_processors/new',
  '/order_post_processors/:id': '/docs/admin/order_post_processors/show',
  '/order_post_processors/:id/edit': '/docs/admin/order_post_processors/edit',

  // Sluggish Products
  '/sluggish_products': '/docs/admin/sluggish_products/query',

  // Unknown Products
  '/unknown_products': '/docs/admin/unknown_products/list',
  '/unknown_products/:id': '/docs/admin/unknown_products/list',

  // Inventories (Current Stock)
  '/stocks': '/docs/admin/inventories/list',

  // Inventory Snapshots
  '/inventory_snapshots': '/docs/admin/inventory_snapshots/list',

  // Inventory Movements
  '/inventory_movements': '/docs/admin/inventory_movements/list',

  // Inventory Monitors (Stock Trackers)
  '/inventory_monitors': '/docs/admin/inventory_monitors/list',
  '/inventory_monitors/new': '/docs/admin/inventory_monitors/new',
  '/inventory_monitors/:id': '/docs/admin/inventory_monitors/show',
  '/inventory_monitors/:id/edit': '/docs/admin/inventory_monitors/new',

  // Return Orders
  '/return_orders': '/docs/admin/return_orders/list',
  '/return_orders/new': '/docs/admin/return_orders/new',
  '/return_orders/:id': '/docs/admin/return_orders/show',
  '/return_orders/:id/edit': '/docs/admin/return_orders/edit',

  // Shipments
  '/shipments': '/docs/admin/shipments/list',
  '/shipments/upload_labels': '/docs/admin/shipments/upload_labels',

  // Consumables
  '/consumables': '/docs/admin/consumables/list',
  '/consumables/new': '/docs/admin/consumables/new',
  '/consumables/:id': '/docs/admin/consumables/show',
  '/consumables/:id/edit': '/docs/admin/consumables/edit',

  // Shop Inventory Types (Inventory Type Settings)
  '/shop_inventory_types': '/docs/admin/shop_inventory_types/list',

  // Sales Channels
  '/sales_channels': '/docs/admin/sales_channels/list',
  '/sales_channels/new': '/docs/admin/sales_channels/new',
  '/sales_channels/:id': '/docs/admin/sales_channels/show',
  '/sales_channels/:id/edit': '/docs/admin/sales_channels/edit',

  // Inventory Attribute Definitions (Custom Attributes)
  '/inventory_attribute_definitions': '/docs/admin/inventory_attribute_definitions/list',

  // Product Attribute Definitions (Custom Attributes)
  '/product_attribute_definitions': '/docs/admin/product_attribute_definitions/list',

  // Notifications
  '/notifications': '/docs/admin/notifications/list',
  '/notifications/:id': '/docs/admin/notifications/show',

  // Workflows (Custom Tools)
  '/workflows': '/docs/admin/workflows/list',
};

/**
 * Operator 角色的文檔映射
 * 路徑格式：實際頁面路徑 → Docs 文檔路徑
 */
const operatorDocsMap = {
  // Products
  '/products': '/docs/operator/products/list',
  '/products/new': '/docs/operator/products/new',
  '/products/:id': '/docs/operator/products/show',
  '/products/:id/edit': '/docs/operator/products/edit',

  // Brands
  '/brands': '/docs/operator/brands/list',
  '/brands/new': '/docs/operator/brands/list',
  '/brands/:id': '/docs/operator/brands/list',

  // Bundles
  '/bundles': '/docs/operator/bundles/list',
  '/bundles/new': '/docs/operator/bundles/new',
  '/bundles/:id': '/docs/operator/bundles/show',
  '/bundles/:id/edit': '/docs/operator/bundles/edit',

  // Product Categories
  '/product_categories': '/docs/operator/product_categories/list',
  '/product_categories/new': '/docs/operator/product_categories/new',
  '/product_categories/:id': '/docs/operator/product_categories/show',
  '/product_categories/:id/edit': '/docs/operator/product_categories/edit',

  // Flexible Products
  '/flexible_products': '/docs/operator/flexible_products/list',
  '/flexible_products/new': '/docs/operator/flexible_products/new',
  '/flexible_products/:id': '/docs/operator/flexible_products/show',
  '/flexible_products/:id/edit': '/docs/operator/flexible_products/edit',

  // Sluggish Products
  '/sluggish_products': '/docs/operator/sluggish_products/query',

  // Product Label Templates
  '/product_label_templates': '/docs/operator/product_label_templates/list',
  '/product_label_templates/new': '/docs/operator/product_label_templates/new',
  '/product_label_templates/:id': '/docs/operator/product_label_templates/show',
  '/product_label_templates/:id/edit': '/docs/operator/product_label_templates/edit',

  // Orders
  '/orders': '/docs/operator/orders/list',
  '/orders/new': '/docs/operator/orders/new',
  '/orders/:id': '/docs/operator/orders/show',
  '/orders/:id/edit': '/docs/operator/orders/edit',

  // Inventories
  '/inventories': '/docs/operator/inventories/list',

  // Inventory Snapshots
  '/inventory_snapshots': '/docs/operator/inventory_snapshots/list',

  // Location Snapshots
  '/location_snapshots': '/docs/operator/location_snapshots/list',

  // Inventory Movements
  '/inventory_movements': '/docs/operator/inventory_movements/list',

  // Inventory Monitors
  '/inventory_monitors': '/docs/operator/inventory_monitors/list',
  '/inventory_monitors/new': '/docs/operator/inventory_monitors/new',
  '/inventory_monitors/:id': '/docs/operator/inventory_monitors/show',
  '/inventory_monitors/:id/edit': '/docs/operator/inventory_monitors/edit',

  // Shelves
  '/shelves': '/docs/operator/shelves/list',
  '/shelves/batch_new': '/docs/operator/shelves/new',
  '/shelves/:id': '/docs/operator/shelves/list',

  // Shelf Movements
  '/shelf_movements': '/docs/operator/shelf_movements/list',

  // Inbounds
  '/inbounds': '/docs/operator/inbounds/list',
  '/inbounds/new': '/docs/operator/inbounds/new',
  '/inbounds/:id': '/docs/operator/inbounds/show',
  '/inbounds/:id/edit': '/docs/operator/inbounds/edit',

  // Notifications
  '/notifications': '/docs/operator/notifications/list',
  '/notifications/:id': '/docs/operator/notifications/list',

  // Operations
  '/operations/distribute': '/docs/operator/operations/distribute',
  '/operations/fulfillment_qc': '/docs/operator/operations/fulfillment_qc',
  '/operations/inbound_acceptance': '/docs/operator/operations/inbound_acceptance',

  // Unknown Products
  '/unknown_products': '/docs/operator/unknown_products/list',
  '/unknown_products/:id': '/docs/operator/unknown_products/list',

  // Out of Stock Products
  '/orders/out_of_stock': '/docs/operator/out_of_stock_products/list',

  // Order Templates
  '/order_templates': '/docs/operator/order_templates/list',
  '/order_templates/new': '/docs/operator/order_templates/new',
  '/order_templates/:id': '/docs/operator/order_templates/list',
  '/order_templates/:id/edit': '/docs/operator/order_templates/new',

  // Fulfillment Merge
  '/fulfillments/merge': '/docs/operator/fulfillments/merge',

  // Contact Addresses
  '/contact_addresses': '/docs/operator/contact_addresses/list',
  '/contact_addresses/new': '/docs/operator/contact_addresses/new',
  '/contact_addresses/:id': '/docs/operator/contact_addresses/show',
  '/contact_addresses/:id/edit': '/docs/operator/contact_addresses/edit',

  // Warehouse Broadcasts
  '/warehouse_broadcasts': '/docs/operator/warehouse_broadcasts/list',

  // Operator master data
  '/master_data/inventory_types': '/docs/operator/inventory_types/list',
  '/data/inventory_types': '/docs/operator/inventory_types/list',
  '/master_data/merchant_inventory_types': '/docs/operator/merchant_inventory_types/list',
  '/data/merchant_inventory_types': '/docs/operator/merchant_inventory_types/list',
  '/master_data/shipping_accounts': '/docs/operator/shipping_accounts/list',
  '/system_settings/shipping_accounts': '/docs/operator/shipping_accounts/list',

  // Merchant Channels
  '/data/sales_channels': '/docs/operator/sales_channels/list',
  '/data/sales_channels/:id': '/docs/operator/sales_channels/show',
  '/data/sales_channels/:id/edit': '/docs/operator/sales_channels/edit',
  '/sales_channels': '/docs/operator/sales_channels/list',
  '/sales_channels/:id': '/docs/operator/sales_channels/show',
  '/sales_channels/:id/edit': '/docs/operator/sales_channels/edit',

  // Merchants
  '/merchants': '/docs/operator/merchants/list',
  '/merchants/:id': '/docs/operator/merchants/show',

  // Billings
  '/billings': '/docs/operator/billings/list',
  '/billings/:id': '/docs/operator/billings/show',

  // Operators (Staff Management)
  '/operators': '/docs/operator/operators/list',
  '/operators/:id/edit': '/docs/operator/operators/edit',
  '/operators/:id/merchants': '/docs/operator/operators/merchants',

  // Warehouses
  '/warehouses': '/docs/operator/warehouses/list',
  '/warehouses/:id': '/docs/operator/warehouses/show',

  // Serial Numbers
  '/orders/serial_numbers': '/docs/operator/orders/serial_numbers',

  // Stock Transfer Orders
  '/stock_transfer_orders': '/docs/operator/stock_transfer_orders/list',
  '/stock_transfer_orders/new': '/docs/operator/stock_transfer_orders/list',
  '/stock_transfer_orders/:id': '/docs/operator/stock_transfer_orders/list',
  '/stock_transfer_orders/:id/edit': '/docs/operator/stock_transfer_orders/list',

  // Reports
  '/reports/slotting_analysis': '/docs/operator/reports/slotting_analysis',
};

/**
 * 將路徑模式轉換為正則表達式
 * @param {string} pattern - 路徑模式，例如 '/products/:id'
 * @returns {RegExp} 正則表達式
 */
const patternToRegex = (pattern) => {
  const regexPattern = pattern
    .replace(/\//g, '\\/')  // 轉義斜線
    .replace(/:\w+/g, '[^/]+');  // :id 替換為 [^/]+ (匹配任何非斜線字符)
  return new RegExp(`^${regexPattern}$`);
};

/**
 * 根據當前路徑和角色獲取對應的文檔連結
 * @param {string} pathname - 當前頁面路徑
 * @param {string} role - 角色 'admin' 或 'operator'
 * @returns {string|null} 文檔 URL，如果找不到則返回 null
 */
export const getManualUrl = (pathname, role = 'admin') => {
  const docsMap = role === 'operator' ? operatorDocsMap : adminDocsMap;

  // 先嘗試精確匹配
  if (docsMap[pathname]) {
    return docsMap[pathname];
  }

  // 如果精確匹配失敗，嘗試模式匹配
  for (const [pattern, docsUrl] of Object.entries(docsMap)) {
    const regex = patternToRegex(pattern);
    if (regex.test(pathname)) {
      return docsUrl;
    }
  }

  return null;
};

/**
 * 檢查當前頁面是否有對應的說明文檔
 * @param {string} pathname - 當前頁面路徑
 * @param {string} role - 角色 'admin' 或 'operator'
 * @returns {boolean} 是否有對應文檔
 */
export const hasManual = (pathname, role = 'admin') => {
  return getManualUrl(pathname, role) !== null;
};

/**
 * 獲取角色的文檔首頁 URL
 * @param {string} role - 角色 'admin' 或 'operator'
 * @returns {string} 角色文檔首頁 URL
 */
export const getRoleDocsHome = (role = 'admin') => {
  return `/docs/${role}`;
};

/**
 * 獲取文檔 URL（如果當前頁面沒有對應文檔，返回角色首頁）
 * @param {string} pathname - 當前頁面路徑
 * @param {string} role - 角色 'admin' 或 'operator'
 * @returns {string} 文檔 URL
 */
export const getManualUrlOrHome = (pathname, role = 'admin') => {
  const manualUrl = getManualUrl(pathname, role);
  return manualUrl || getRoleDocsHome(role);
};
